/* __next_internal_client_entry_do_not_use__ ToastProvider,useToasts auto */ import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { Portal } from "@air/primitive-portal";
import { animated, config, useTransition } from "@react-spring/web";
import classNames from "classnames";
import constant from "lodash/constant";
import isFunction from "lodash/isFunction";
import noop from "lodash/noop";
import uniqueId from "lodash/uniqueId";
import { createContext, memo, useCallback, useContext, useMemo, useRef, useState } from "react";
import { ToastItem } from "./ToastItem";
const createToast = (content, options)=>({
        id: uniqueId("toast"),
        content,
        options
    });
const toastOptionDefaults = {
    color: "black",
    onDismiss: noop,
    prefix: null,
    suffix: null,
    timeBeforeAutoDismiss: 5000,
    withCloseButton: false,
    type: "polite"
};
const defaultValue = {
    showToast: noop,
    dismissAllToasts: noop,
    setToastBottomOffset: noop,
    setToastXOffset: noop,
    getToastXOffset: constant(0)
};
const ToastContext = /*#__PURE__*/ createContext(defaultValue);
export const ToastProvider = /*#__PURE__*/ memo((param)=>{
    let { children, renderToast } = param;
    const [toasts, setToasts] = useState([]);
    const [bottomOffset, setToastBottomOffset] = useState(0);
    const toastXOffsetRef = useRef(0);
    const dismissAllToasts = useCallback(()=>{
        // do not set an empty array if there are no toast to avoid unnecessary rerenders on route change
        setToasts((currentToasts)=>currentToasts.length === 0 ? currentToasts : []);
    }, []);
    const clearToast = (id)=>setToasts((current)=>current.filter((t)=>t.id !== id));
    const value = useMemo(()=>({
            dismissAllToasts,
            showToast: (content, options)=>{
                setToasts((currentToasts)=>[
                        ...currentToasts,
                        createToast(content, options)
                    ]);
            },
            setToastBottomOffset,
            setToastXOffset: (offset)=>{
                toastXOffsetRef.current = offset;
            },
            getToastXOffset: ()=>toastXOffsetRef.current
        }), [
        dismissAllToasts,
        toastXOffsetRef
    ]);
    const transitions = useTransition(toasts, {
        keys: (item)=>item.id,
        from: {
            opacity: 0,
            y: 16
        },
        enter: {
            opacity: 1,
            y: 0
        },
        leave: {
            opacity: 0,
            y: 16
        },
        config: config.stiff
    });
    return /*#__PURE__*/ _jsxs(ToastContext.Provider, {
        value: value,
        children: [
            children,
            /*#__PURE__*/ _jsx(Portal, {
                children: /*#__PURE__*/ _jsx("ul", {
                    className: classNames("pointer-events-none fixed bottom-0 right-0 z-[10] m-0 flex flex-col items-center overflow-hidden px-0 py-3"),
                    style: {
                        width: "calc(100% - ".concat(toastXOffsetRef.current, "px)"),
                        marginBottom: bottomOffset
                    },
                    children: transitions((style, item)=>{
                        const { color, onDismiss, prefix, suffix, timeBeforeAutoDismiss, withCloseButton, type } = {
                            ...toastOptionDefaults,
                            ...item.options
                        };
                        const dismissHandler = ()=>{
                            onDismiss();
                            clearToast(item.id);
                        };
                        const children = isFunction(item.content) ? item.content({
                            onDismiss: dismissHandler
                        }) : item.content;
                        return /*#__PURE__*/ _jsx(animated.div, {
                            className: "pointer-events-auto list-none",
                            style: style,
                            children: !!renderToast ? renderToast({
                                children,
                                color,
                                onDismiss: dismissHandler,
                                prefix,
                                suffix,
                                timeBeforeAutoDismiss,
                                type,
                                withCloseButton
                            }) : /*#__PURE__*/ _jsx(ToastItem, {
                                color: color,
                                onDismiss: dismissHandler,
                                prefix: prefix,
                                suffix: suffix,
                                type: type,
                                timeBeforeAutoDismiss: timeBeforeAutoDismiss,
                                withCloseButton: withCloseButton,
                                children: children
                            })
                        });
                    })
                })
            })
        ]
    });
});
ToastProvider.displayName = "ToastProvider";
export const useToasts = ()=>{
    const context = useContext(ToastContext);
    if (context === defaultValue) {
        throw "useToasts used outside of ToastProvider";
    }
    return context;
};
